import { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

/* DnD kit */
import { arrayMove } from '@dnd-kit/sortable';
import {
    MouseSensor,
    TouchSensor,
    closestCenter,
    closestCorners,
    useSensor,
    useSensors,
} from '@dnd-kit/core';

/* Context */
import { useTripContext } from '../components/itinerary/TripContext';
import { useToast } from '../context/ToastContext';

/* Hooks */
import { useReadOnlyToast } from './useReadOnlyToast';

/* Utils */
import { tripUtils } from '../utils/tripUtils';

export const useDnd = (initiateAction) => {

    const { id: tripId } = useParams();
    const { showToast } = useToast();
    const notifyReadOnly = useReadOnlyToast();

    // Grab what we need from context
    const {
        itineraryDays, setItineraryDays,
        setIsDirty,
        fromDateObj,
        canEdit
    } = useTripContext();

    // DnD State for Overlay
    const [activeDragId, setActiveDragId] = useState(null);
    const [activeDragData, setActiveDragData] = useState(null);

    const dragOriginDayId = useRef(null);

    // Define Sensors for Drag and Drop
    const sensors = useSensors(
        // Desktop: start dragging after a small 5px movement (instant feel)
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        // Touch: press-and-hold ~250ms before a drag starts
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        })
    );

    // Collision detection strategy
    const customCollisionDetection = (args) => {
        // If we are dragging a Day, ONLY collide with other Days
        if (args.active.data.current?.type === 'Day') {
            return closestCorners({
                ...args,
                droppableContainers: args.droppableContainers.filter(
                    (container) => container.data.current?.type === 'Day'
                ),
            });
        }

        // Return to standard closestCenter for everything else (Rows, Columns)
        return closestCenter(args);
    };

    // Handle start of drag (when the mouse is pressed)
    const handleDragStart = (event) => {
        if (!canEdit) return notifyReadOnly();
        const { active } = event;
        setActiveDragId(active.id);
        setActiveDragData(active.data.current);

        if (active.data.current?.type === 'Row') {
            dragOriginDayId.current = active.data.current.sortable?.containerId;   // the day it started in
        }
    };

    // Handle moving rows between different days
    const handleDragOver = (event) => {
        if (!canEdit) return;
        const { active, over } = event;
        if (!over) return;

        // Only handle row dragging for now
        if (active.data.current?.type !== 'Row') return;

        const activeId = active.id;
        const overId = over.id;

        // Find containers
        const findContainer = (id) => {
            // Check if it's a day ID directly (empty day container)
            const dayDirect = itineraryDays.find(d => d.id === id);
            if (dayDirect) return dayDirect.id;

            // Otherwise find which day contains the row
            const dayWithRow = itineraryDays.find(d => d.rows.some(r => r.id === id));
            return dayWithRow?.id;
        };

        const activeContainer = findContainer(activeId);
        const overContainer = findContainer(overId);

        // If it's the same container, do nothing
        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return;
        }

        // Moving to a new day container
        setItineraryDays((prev) => {
            const activeDayIndex = prev.findIndex((d) => d.id === activeContainer);
            const overDayIndex = prev.findIndex((d) => d.id === overContainer);

            return prev.map((day, index) => {

                // Handle the source day
                if (index === activeDayIndex) {
                    // Remove the dragged away row from the source day
                    return {
                        ...day,
                        rows: day.rows.filter((r) => r.id !== activeId),
                    };
                }

                // Handle the target day
                if (index === overDayIndex) {
                    // Add to target
                    const activeRow = prev[activeDayIndex].rows.find((r) => r.id === activeId);

                    // Insert logic
                    let newRows = [...day.rows];
                    const overRowIndex = newRows.findIndex((r) => r.id === overId);

                    if (overRowIndex >= 0) {
                        // Dropped over specific row
                        const isBelowOverItem =
                            over &&
                            active.rect.current.translated &&
                            active.rect.current.translated.top > over.rect.top + over.rect.height;

                        const modifier = isBelowOverItem ? 1 : 0;
                        newRows.splice(overRowIndex + modifier, 0, activeRow);
                    } else {
                        // Dropped into empty container or non-row area
                        newRows.push(activeRow);
                    }

                    return { ...day, rows: newRows };
                }
                // Ignore non-applicable days
                return day;
            });
        });
    };

    // Handle end of drag (when the mouse is released)
    const handleDragEnd = (event) => {
        if (!canEdit) return;
        const { active, over } = event;

        setActiveDragId(null);
        setActiveDragData(null);

        // Clear drag overlay/state if needed
        if (!over) return;

        // Handle row dragging
        if (active.data.current?.type === 'Row') {
            const activeId = active.id;
            const overId = over.id;

            const findContainer = (id) => {
                // Check if it's a day ID directly (empty day container)
                const dayDirect = itineraryDays.find(d => d.id === id);
                if (dayDirect) return dayDirect.id;

                // Otherwise find which day contains the row
                const dayWithRow = itineraryDays.find(d => d.rows.some(r => r.id === id));
                return dayWithRow?.id;
            };

            // Find out which day the active item started in / moved to
            const activeContainer = findContainer(activeId);
            const overContainer = findContainer(overId);

            // Find out which day the active item started in / moved to
            const originDayId = dragOriginDayId.current;
            const currentDayId = findContainer(activeId);

            // ---- CROSS-DAY MOVE ----
            if (originDayId && currentDayId && originDayId !== currentDayId) {
                setIsDirty(true);
                if (tripId) {
                    const currentDay = itineraryDays.find(d => d.id === currentDayId);
                    const originDay = itineraryDays.find(d => d.id === originDayId);
                    const movedRow = currentDay?.rows.find(r => r.id === activeId);
                    if (movedRow && currentDay && originDay) {
                        tripUtils.createItem(tripId, currentDayId, movedRow)      // upsert now moves day_id
                            .then(() => Promise.all([
                                tripUtils.syncItemOrder(tripId, currentDayId, currentDay.rows.map(r => r.id)),
                                tripUtils.syncItemOrder(tripId, originDayId, originDay.rows.map(r => r.id)),
                            ]))
                            .catch(() => showToast('Failed to move row', 'error'));
                    }
                }
                return;   // handled — skip the same-day reorder logic below
            }

            // ---- SAME-DAY REORDER ----
            if (activeContainer && overContainer && activeContainer === overContainer) {

                // Reordering within the same day
                const dayIndex = itineraryDays.findIndex((d) => d.id === activeContainer);
                const day = itineraryDays[dayIndex];

                const oldIndex = day.rows.findIndex((r) => r.id === activeId);
                const newIndex = day.rows.findIndex((r) => r.id === overId);

                if (oldIndex !== newIndex) {
                    setIsDirty(true);

                    const reordered = arrayMove(day.rows, oldIndex, newIndex);

                    setItineraryDays((prev) => {
                        const newDays = [...prev];
                        newDays[dayIndex] = { ...day, rows: reordered };
                        return newDays;
                    });

                    if (tripId) {
                        tripUtils.syncItemOrder(tripId, day.id, reordered.map(r => r.id))
                            .catch(() => showToast('Failed to save order', 'error'));
                    }
                }
            }
        }

        // Handle column dragging
        if (active.data.current?.type === 'Column') {
            const activeCompositeId = active.id;
            const overCompositeId = over.id;

            // Parse real column IDs
            const getRealColId = (compositeId) => {
                if (!compositeId) return '';
                return String(compositeId).split(':').pop();
            };
            // Get the real column IDs without the prefix 'day-n:'
            const activeRealId = getRealColId(activeCompositeId);
            const overRealId = getRealColId(overCompositeId);

            // Find which day/container these columns belong to
            const activeContainer = active.data.current.sortable.containerId;
            const overContainer = over.data.current?.sortable?.containerId;

            // Strict check: Must be in same container (same day)
            if (activeContainer && overContainer && activeContainer === overContainer) {
                // Robustly find the day containing this column
                const dayIndex = itineraryDays.findIndex(d => d.id === activeContainer);

                if (dayIndex === -1) return;

                const day = itineraryDays[dayIndex];
                const oldIndex = day.columns.findIndex(c => c.id === activeRealId);
                const newIndex = day.columns.findIndex(c => c.id === overRealId);

                if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                    initiateAction('REORDER_COLUMN', {
                        dayIndex,
                        activeId: activeRealId,
                        overId: overRealId,
                        newIndex
                    });
                }
            }
        }

        // Handle day dragging
        if (active.data.current?.type === 'Day') {
            const activeId = active.id;
            const overId = over.id;

            if (activeId !== overId) {
                const oldIndex = itineraryDays.findIndex(d => d.id === activeId);
                const newIndex = itineraryDays.findIndex(d => d.id === overId);

                if (oldIndex !== -1 && newIndex !== -1) {
                    setIsDirty(true);
                    const reordered = arrayMove(itineraryDays, oldIndex, newIndex).map((day, index) => {
                        const newDate = new Date(fromDateObj);
                        newDate.setDate(newDate.getDate() + index);
                        return { ...day, date: newDate };
                    });
                    setItineraryDays(reordered);

                    if (tripId) {
                        tripUtils.syncDayOrder(tripId, reordered.map(d => d.id))
                            .catch(() => showToast('Failed to save order', 'error'));
                    }
                }
            }
        }
    };

    return {
        sensors,
        customCollisionDetection,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        activeDragId,
        activeDragData
    }
};